import {
  EntityPlugin,
  SYSTEM_CHANNELS,
  type DataSource,
  type EntityPluginContext,
  type EntityTypeConfig,
  type Template,
} from "@brains/plugins";
import {
  SUMMARY_RETIRED_FIELDS,
  summarySchema,
  type SummaryEntity,
} from "./schemas/summary";
import {
  summaryConfigSchema,
  type SummaryConfig,
  type SummaryConfigInput,
} from "./schemas/summary-config";
import { SummaryAdapter } from "./adapters/summary-adapter";
import {
  ACTION_ITEM_ENTITY_TYPE,
  ACTION_ITEM_RETIRED_FIELDS,
  ACTION_ITEM_TRANSLATED_FIELDS,
  ActionItemAdapter,
  actionItemSchema,
} from "@brains/action-items";
import { DecisionAdapter } from "./adapters/conversation-memory-adapters";
import {
  DECISION_RETIRED_FIELDS,
  decisionSchema,
} from "./schemas/conversation-memory";
import { summaryListTemplate } from "./templates/summary-list";
import { summaryDetailTemplate } from "./templates/summary-detail";
import { channelActionItemsTemplate } from "./templates/channel-action-items";
import { SummaryDataSource } from "./datasources/summary-datasource";
import { registerActionItemsWidget } from "./lib/widgets/action-items";
import { registerDecisionsWidget } from "./lib/widgets/decisions";
import { registerRecentConversationMemoryWidget } from "./lib/widgets/recent-memory-register";
import { registerConversationMemoryAgentContext } from "./lib/agent-context-provider";
import { registerMemoryEvalHandlers } from "./lib/eval-handlers";
import { flattenMemoryIds } from "./lib/flat-memory-ids";
import { registerChannelActionItems } from "./lib/channel-action-items/register";
import {
  DECISION_ENTITY_TYPE,
  SUMMARY_ENTITY_TYPE,
  SUMMARY_PLUGIN_ID,
} from "./lib/constants";
import packageJson from "../package.json";

const summaryAdapter: SummaryAdapter = new SummaryAdapter();
const decisionAdapter: DecisionAdapter = new DecisionAdapter();
const actionItemAdapter: ActionItemAdapter = new ActionItemAdapter();

export class ConversationMemoryPlugin extends EntityPlugin<
  SummaryEntity,
  SummaryConfig,
  SummaryConfigInput
> {
  readonly entityType: typeof SUMMARY_ENTITY_TYPE = SUMMARY_ENTITY_TYPE;
  readonly schema: typeof summarySchema = summarySchema;
  readonly adapter: typeof summaryAdapter = summaryAdapter;

  declare protected config: SummaryConfig;

  constructor(config: SummaryConfigInput = {}) {
    super(SUMMARY_PLUGIN_ID, packageJson, config, summaryConfigSchema, {
      [SUMMARY_ENTITY_TYPE]: {
        create: "never",
        update: "never",
        delete: "never",
        extract: "never",
        publish: "never",
      },
    });
  }

  public getConfig(): SummaryConfig {
    return this.config;
  }

  protected override getTemplates(): Record<string, Template> {
    return {
      "summary-list": summaryListTemplate,
      "summary-detail": summaryDetailTemplate,
      "channel-action-items": channelActionItemsTemplate,
    };
  }

  protected override getDataSources(): DataSource[] {
    return [new SummaryDataSource(this.logger.child("SummaryDataSource"))];
  }

  protected override getEntityTypeConfig(): EntityTypeConfig {
    return {
      projectionSource: false,
      projectionSourceRole: "excluded",
      retiredFields: SUMMARY_RETIRED_FIELDS,
    };
  }

  protected override async onRegister(
    context: EntityPluginContext,
  ): Promise<void> {
    context.entities.register(
      DECISION_ENTITY_TYPE,
      decisionSchema,
      decisionAdapter,
      { retiredFields: DECISION_RETIRED_FIELDS },
    );
    context.entities.register(
      ACTION_ITEM_ENTITY_TYPE,
      actionItemSchema,
      actionItemAdapter,
      {
        retiredFields: ACTION_ITEM_RETIRED_FIELDS,
        translatedFields: ACTION_ITEM_TRANSLATED_FIELDS,
        // A card is only whole with its origin, capture time and status,
        // which only the action-item writers set: never system_create.
        actionPolicy: { create: "never" },
      },
    );

    // Moved once the startup import has every stored file's entity in place.
    context.messaging.subscribe(
      SYSTEM_CHANNELS.startupContentSettled,
      async () => {
        const moved = await flattenMemoryIds(context.entityService);
        if (moved > 0) {
          this.logger.info(
            `Moved ${moved} action items and decisions to flat ids`,
          );
        }
        return { success: true };
      },
    );

    registerChannelActionItems(context, {
      logger: this.logger,
      visibility: this.config.memoryVisibility,
    });

    registerActionItemsWidget({ context });
    registerDecisionsWidget({ context });
    registerRecentConversationMemoryWidget({ context });

    registerConversationMemoryAgentContext(context);

    registerMemoryEvalHandlers({ context });
  }
}

export function conversationMemoryPlugin(
  config: SummaryConfigInput = {},
): ConversationMemoryPlugin {
  return new ConversationMemoryPlugin(config);
}
